import axios from "axios";
import { ZodError } from "zod";

export const CONTRACT_VIOLATION_USER_MESSAGE =
  "Server returned an unexpected response. Please try again or contact support.";

export const OFFLINE_USER_MESSAGE = "No internet connection. Check your network and try again.";

export const getApiResponseErrorMsg = (e: unknown, context?: string): string => {
  let message = "Something went wrong, please try again later.";

  if (e instanceof ZodError) {
    console.error(`[Contract violation${context ? ` in ${context}` : ""}]`, e.issues);
    return CONTRACT_VIOLATION_USER_MESSAGE;
  }

  /**
   * A request that produced no response at all, made while the browser reports
   * no connection. Axios surfaces this as the bare string "Network Error",
   * which reads to a user as though the server broke — this says whose problem
   * it actually is, and what to do about it.
   *
   * Both halves of the condition matter: `!e.response` alone also covers
   * timeouts and CORS failures on a perfectly good connection, which are not
   * the user's network and should keep their existing message.
   */
  if (
    axios.isAxiosError(e) &&
    !e.response &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    return OFFLINE_USER_MESSAGE;
  }

  if (axios.isAxiosError(e) && e.response) {
    message = `${e.response?.data?.message || e.response?.statusText || message}`;
  } else if (e instanceof Error) {
    message = e.message;
  }

  return message;
};
